import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './FaceCapture.css';
import { ArrowForwardRounded } from '@mui/icons-material';
import { useIdentification } from './IdentificationContext';
import config from './config';
import CameraCapture from './components/CameraCapture';
import { BackButton, EnterpriseCard, EnterpriseHero, EnterpriseShell, EnterpriseTopBar } from './ui/EnterpriseLayout';

function FaceCapture() {
  const navigate = useNavigate();
  const { identificationId } = useIdentification();
  const videoRef = useRef(null);
  const [instruction, setInstruction] = useState('Position your face');
  const [isCapturing, setIsCapturing] = useState(false);
  
  const guideMode = (() => {
    const t = (instruction || '').toLowerCase();
    // Video is now mirrored, so guide directions match the instruction directly
    if (t.includes('look left')) return 'left';
    if (t.includes('look right')) return 'right';
    return 'none';
  })();

  const handleCapture = async (blob) => {
    setIsCapturing(true);
    setInstruction('Capturing...');
    
    try {
      if (!identificationId) {
        throw new Error('No identification ID available');
      }

      const formData = new FormData();
      formData.append('file', blob, 'face.jpg');
      formData.append('documentType', 'FACE');

      const response = await fetch(`${config.API_BASE_URL}/identifications/${identificationId}/files`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload face image');
      }

      const fileData = await response.json();
      console.log('Face image uploaded successfully:', fileData);
      
      // Navigate to result screen with face scan result data
      setTimeout(() => {
        navigate('/age-verified', { 
          state: { 
            faceScanResult: fileData,
            isIdVerification: true
          } 
        });
      }, 500);
    } catch (error) {
      console.error('Error capturing face:', error);
      setIsCapturing(false);
      setInstruction('Error capturing. Please try again.');
      alert('Failed to capture face. Please try again.');
    }
  };

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton onClick={() => navigate('/consent')} />
            <span className="vt-chip vt-chip--subtle">ID verification</span>
          </>
        }
      />

      <EnterpriseHero
        title="Position your face"
        subtitle="Follow the on-screen guidance. This scan is private and short-lived."
      />

      <EnterpriseCard>
        <div className="face-capture-content">
          <div className="camera-container">
            <div className={`liveness-guide ${guideMode !== 'none' ? 'show' : ''}`}>
              <div className={`liveness-guide-side left ${guideMode === 'left' ? 'active' : ''}`} />
              <div className={`liveness-guide-side right ${guideMode === 'right' ? 'active' : ''}`} />
            </div>
            <div className="scan-camera-wrapper">
              <CameraCapture
                mode="face"
                facingMode="user"
                onCapture={handleCapture}
                showCameraSelector={true}
                instruction={instruction}
                onInstructionChange={setInstruction}
                autoCapture={true}
                videoRef={videoRef}
              />
            </div>
            <img src="/Face-No-Background.png" alt="Face outline" className="face-outline-image" />
            {instruction && <div className="instruction-bubble">{instruction}</div>}
            {isCapturing && (
              <div className="capturing-overlay">
                <div className="capturing-spinner"></div>
              </div>
            )}
          </div>

          <p className="vt-note">Keep your face centered until the capture completes.</p>
          <div className="vt-actions" style={{ gridTemplateColumns: '1fr' }}>
            <button className="vt-btn vt-btn--secondary" type="button" onClick={() => navigate('/')}>
              Back to options <ArrowForwardRounded className="vt-btnIcon" />
            </button>
          </div>
        </div>
      </EnterpriseCard>
    </EnterpriseShell>
  );
}

export default FaceCapture;
